import { useState, useCallback, useContext } from 'react';
import { get, post, put, del, patch } from 'aws-amplify/api';
import { AccountContext } from '../context/AccountContext';
import { isCdkProxyConfigured } from '../lib/appConfig';
import { fetchCdkApi, splitProxyUrl } from '../lib/cdkProxy';
import { isGen2NuatalkerConfigured } from '../gen2Api';
import { auditCallerHeaders } from '../services/auditCallerHeaders';

const toHttpMethod = (method) => {
  const lower = String(method || 'GET').toLowerCase();
  if (lower === 'del' || lower === 'delete') return 'DELETE';
  return lower.toUpperCase();
};

const useApi = () => {
  const account = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const callApi = useCallback(
    async ({
      apiName,
      path,
      method = 'get',
      headers = {},
      body,
      queryParams,
    }) => {
      setIsLoading(true);
      setError(null);
      try {
        let fullPath = path;
        if (path.includes('{hubSpotId}')) {
          const hubSpotId = account?.accountId;
          if (!hubSpotId) {
            throw new Error('No hay cuenta activa para resolver {hubSpotId}');
          }
          fullPath = path.replace('{hubSpotId}', hubSpotId);
        }
        const methodLower = method.toLowerCase();
        if (
          isCdkProxyConfigured() &&
          (!apiName || apiName === 'nuatalker' || apiName === 'dialer')
        ) {
          const { path: cleanPath, queryParams: pathQuery } =
            splitProxyUrl(fullPath);
          const result = await fetchCdkApi({
            path: cleanPath,
            method: toHttpMethod(method),
            body,
            queryParams: { ...pathQuery, ...queryParams },
          });
          setIsLoading(false);
          return result;
        }
        const apiMethod = { get, post, put, patch, delete: del, del }[
          methodLower
        ];
        if (!apiMethod) throw new Error(`HTTP method not supported: ${method}`);
        const options = {
          headers: {
            ...(isGen2NuatalkerConfigured() ? await auditCallerHeaders() : {}),
            ...headers,
          },
          ...(queryParams ? { queryParams } : {}),
          ...(['post', 'put', 'patch'].includes(methodLower) &&
          body !== undefined
            ? { body }
            : {}),
        };
        const { body: bodyResponse } = await apiMethod({
          apiName,
          path: fullPath,
          options,
        }).response;
        setIsLoading(false);
        try {
          return await bodyResponse.json();
        } catch {
          return null;
        }
      } catch (err) {
        setIsLoading(false);
        const safeError =
          err?._response?.body ?? err?.message ?? 'Error desconocido';
        console.error('API error', safeError, err);
        setError(safeError);
        throw err;
      }
    },
    [account?.accountId]
  );

  return { isLoading, error, callApi };
};

export default useApi;
